import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PublicLayout, PageContainer, PageHeader } from "@/components/PublicLayout";
import { useSeo } from "@/lib/useSeo";
import { LoadingState, ErrorState } from "@/components/States";
import { CaretLeft, CaretRight, Target } from "@phosphor-icons/react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function TeerCalendar() {
  useSeo({ title: "Guwahati Morning Teer Calendar | Teer Club Chart by Date", description: "Browse Guwahati Morning Teer results by date on the calendar. Tap any date to see the First Round and Second Round result for that day.", path: "/calendar" });
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selected, setSelected] = useState(null);

  const q = useQuery({ queryKey: ["results"], queryFn: async () => (await api.get("/results")).data });

  const byDate = useMemo(() => {
    const map = {};
    (q.data || []).forEach((r) => { map[r.date] = r; });
    return map;
  }, [q.data]);

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const prev = () => { setSelected(null); if (month === 0) { setMonth(11); setYear((y) => y - 1); } else setMonth((m) => m - 1); };
  const next = () => { setSelected(null); if (month === 11) { setMonth(0); setYear((y) => y + 1); } else setMonth((m) => m + 1); };

  const selectedResult = selected ? byDate[selected] : null;

  return (
    <PublicLayout>
      <PageContainer className="py-10">
        <PageHeader eyebrow="Club Chart" title="Teer Club Chart" subtitle="Tap any date on the calendar to instantly see the Morning Teer result for that day." />

        {q.isLoading ? (
          <LoadingState />
        ) : q.isError ? (
          <ErrorState onRetry={q.refetch} />
        ) : (
          <div className="max-w-xl mx-auto">
            <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_10px_40px_-10px_rgba(76,29,149,0.12)] overflow-hidden" data-testid="calendar">
              <div className="flex items-center justify-between bg-gradient-to-r from-brand-900 to-brand text-white px-4 py-4">
                <button onClick={prev} data-testid="calendar-prev" className="w-10 h-10 rounded-xl hover:bg-white/15 flex items-center justify-center transition-colors">
                  <CaretLeft className="w-5 h-5" weight="bold" />
                </button>
                <h3 className="font-heading font-bold text-lg" data-testid="calendar-title">{MONTHS[month]} {year}</h3>
                <button onClick={next} data-testid="calendar-next" className="w-10 h-10 rounded-xl hover:bg-white/15 flex items-center justify-center transition-colors">
                  <CaretRight className="w-5 h-5" weight="bold" />
                </button>
              </div>
              <div className="grid grid-cols-7 border-b border-slate-100">
                {DOW.map((d) => (
                  <div key={d} className="text-center py-2.5 text-xs font-bold text-slate-400 uppercase">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-px p-2">
                {cells.map((day, i) => {
                  if (!day) return <div key={i} />;
                  const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const r = byDate[dateStr];
                  const isSel = selected === dateStr;
                  return (
                    <button
                      key={i}
                      onClick={() => r && setSelected(dateStr)}
                      disabled={!r}
                      data-testid={r ? `calendar-day-active-${day}` : `calendar-day-${day}`}
                      className={`aspect-square rounded-xl flex flex-col items-center justify-center text-center transition-colors ${isSel ? "bg-brand text-white ring-2 ring-brand" : r ? "bg-brand-light hover:bg-brand/20 cursor-pointer" : "cursor-default"}`}
                    >
                      <span className={`text-xs font-semibold ${isSel ? "text-white" : r ? "text-brand" : "text-slate-400"}`}>{day}</span>
                      {r && (
                        <span className={`font-heading font-extrabold text-[11px] leading-none mt-0.5 tabular-nums ${isSel ? "text-white" : "text-brand"}`}>
                          {r.fr}·{r.sr}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected date detail */}
            {selectedResult && (
              <div className="mt-5 rounded-2xl overflow-hidden border border-slate-200 shadow-[0_10px_40px_-10px_rgba(76,29,149,0.18)] animate-fade-up" data-testid="calendar-detail">
                <div className="bg-gradient-to-r from-brand-900 to-brand text-white px-5 py-3 flex items-center gap-2">
                  <Target className="w-5 h-5" weight="duotone" />
                  <span className="font-heading font-bold">Result · {selectedResult.date}</span>
                </div>
                <div className="grid grid-cols-2 divide-x divide-slate-200">
                  <div className="py-6 text-center">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">F/R (10:30 AM)</p>
                    <p className="font-heading font-black text-5xl text-brand tabular-nums mt-1" data-testid="calendar-detail-fr">{selectedResult.fr || "--"}</p>
                  </div>
                  <div className="py-6 text-center">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">S/R (11:30 AM)</p>
                    <p className="font-heading font-black text-5xl text-slate-800 tabular-nums mt-1" data-testid="calendar-detail-sr">{selectedResult.sr || "--"}</p>
                  </div>
                </div>
              </div>
            )}
            {selected && !selectedResult && (
              <p className="text-center text-sm text-slate-500 mt-4">No result published for {selected}.</p>
            )}
          </div>
        )}
        <p className="text-center text-xs text-slate-400 mt-4">Highlighted dates have published results. Tap to view.</p>
      </PageContainer>
    </PublicLayout>
  );
}
